export const communityAccess = ["view", "react", "comment", "post"] as const
export type CommunityAccess = (typeof communityAccess)[number]

// Products whose owners can be given access to a community channel.
export const communityChannelProductTypes = ["course", "downloadable", "video", "event"] as const
export type CommunityChannelProductType = (typeof communityChannelProductTypes)[number]

export type CommunityChannelProductAccess = {
	type: CommunityChannelProductType
	id: number
	title: string
	access: CommunityAccess
}

export const communityPostReactionTypes = ["like", "love", "insightful", "motivated", "wellDone"] as const
export type CommunityPostReactionType = (typeof communityPostReactionTypes)[number]

export const communityPostReactionLabels: Record<CommunityPostReactionType, string> = {
	like: "Like",
	love: "Love",
	insightful: "Insightful",
	motivated: "Motivated",
	wellDone: "Well done",
}

export function parseCommunityPostReactionType(value: string): CommunityPostReactionType {
	return communityPostReactionTypes.find(type => type === value) ?? "like"
}

export const communityProfileBioMaxLength = 500

export type ThinCommunityProfile = {
	id: number
	email: string
	firstName: string
	lastName: string
	avatarFilePath: string
	registerDate: { community: Date; user: Date }
}

export type MessageableCommunityProfile = {
	id: number
	firstName: string
	lastName: string
	avatarFilePath: string
	isCreator: boolean
}

export interface PublicCommunityProfile {
	id: number
	platformId: number
	userId: number
	firstName: string
	lastName: string
	bio: string | null
	avatarFilePath: string
	isCreator: boolean
	posts: number
	reactions: number
	comments: number
	gamification: {
		points: number
		currentLevel: {
			tier: number
			label: string
			unlockPoints: number
		} | null
		nextLevel: {
			tier: number
			label: string
			unlockPoints: number
		} | null
	} | null
	registerDate: {
		community: Date
		user: Date
	}
}

export interface CommunityProfile extends PublicCommunityProfile {
	email: string
}

export interface Conversation {
	otherProfile: PublicCommunityProfile
	lastMessage: {
		text: string
		sendDate: Date
	}
}

export type CreatorCommunityNotificationCounts = {
	moderation: number
	posts: number
	messages: number
}

export interface CommunityMessage {
	id: number
	htmlContent: string
	senderProfileId: number
	receiverProfileId: number
	sendDate: Date
}

export type CommunityChannel = {
	id: number | null
	name: string
	defaultAccess: CommunityAccess | null
	membershipAccess: { membershipId: number; access: CommunityAccess }[]
	productAccess: CommunityChannelProductAccess[]
}

export type PersonalizedCommunityChannel = {
	id: number | null
	name: string
	userAccess: CommunityAccess
}

export interface CommunityPostCategory {
	id: number
	label: string
}

export interface DetailedCommunityPostCategory extends CommunityPostCategory {
	platformId: number
	posts: number
}

export type CommunityPost = {
	id: number
	platformId: number
	pinIndex: number | null
	channel: {
		id: number | null
		name: string
		defaultAccess: CommunityAccess | null
		membershipAccess: { membershipId: number; access: CommunityAccess }[]
		productAccess: CommunityChannelProductAccess[]
	}
	profile: {
		id: number
		firstName: string
		lastName: string
		avatarFilePath: string
		isCreator: boolean
	}
	category: { id: number; label: string } | null
	title: string
	htmlContent: string
	images: { id: number; filePath: string }[]
	video: { type: "youtube" | "vimeo"; id: string } | null
	pollOptions: { id: number; label: string; votedProfileIds: number[] }[]
	attachments: { id: number; label: string; filePath: string }[]
	reactions: { profileId: number; type: CommunityPostReactionType }[]
	commentsCount: number
	createDate: Date
	editDate: Date | null
}

export interface RawCommunityPost {
	id: number
	platformId: number
	channelId: number | null
	profileId: number
	categoryId: number | null
	title: string
	content: string
	images: { id: number; filePath: string }[]
	video: { type: "youtube" | "vimeo"; id: string } | null
	hasPoll: boolean
	attachments: { id: number; label: string }[]
}

export type PersonalizedCommunityPost = {
	id: number
	platformId: number
	channel: { id: number | null; name: string; userAccess: CommunityAccess }
	pinIndex: number | null
	profile: {
		id: number
		firstName: string
		lastName: string
		avatarFilePath: string
		isCreator: boolean
	}
	category: { id: number; label: string } | null
	title: string
	htmlContent: string
	images: { id: number; filePath: string }[]
	video: { type: "youtube" | "vimeo"; id: string } | null
	pollOptions: CommunityPostPollOption[]
	attachments: { id: number; label: string; filePath: string }[]
	reactionCounts: Record<CommunityPostReactionType, number>
	commentsCount: number
	createDate: Date
	editDate: Date | null
	userReactionType: CommunityPostReactionType | null
	hasEditAccess: boolean
}

export interface CommunityPostReaction {
	type: CommunityPostReactionType
	profile: PublicCommunityProfile
}

export interface CommunityPostComment {
	parentId: number | null
	id: number
	profile: {
		id: number
		firstName: string
		lastName: string
		avatarFilePath: string
		isCreator: boolean
	}
	htmlContent: string
	reactionCounts: Record<CommunityPostReactionType, number>
	userReactionType: CommunityPostReactionType | null
	createDate: Date
	editDate: Date | null
}

export interface CommunityPostPollOption {
	id: number
	label: string
	votes: { count: number; share: number }
	hasUserSelected: boolean
}

export interface CommunityPostAttachment {
	id: number
	label: string
	filePath: string
}

export interface GamificationLevel {
	id: number
	platformId: number
	label: string
	unlockPoints: number
}
